<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import QuepasaAPI from 'dashboard/api/quepasa';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    SettingsSection,
    NextButton,
    Spinner,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isConnecting: false,
      isSyncing: false,
      isTogglingGroups: false,
      isTogglingCalls: false,
      qrCode: null,
      qrCodeTimer: null,
      countdownTimer: null,
      countdown: 0,
      showToken: false,
      connectionStatus: {
        connected: false,
        wid: null,
        phone: null,
        name: null,
      },
      settings: {
        quepasa_url: '',
        quepasa_token: '',
        quepasa_user: '',
        quepasa_enable_group: false,
        quepasa_accept_call: false,
        quepasa_single_thread: true,
        quepasa_send_agent_name: true,
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    isConnected() {
      return this.inbox.quepasa_connected || this.connectionStatus.connected;
    },
    displayWid() {
      return this.inbox.quepasa_wid || this.connectionStatus.wid;
    },
    isConfigured() {
      // If URL is from env, we can proceed even without token (backend will auto-generate)
      const hasUrl = this.hideUrl || this.settings.quepasa_url;
      // Token is optional - backend auto-generates if not provided
      return hasUrl;
    },
    hideUrl() {
      return this.inbox.hide_url || false;
    },
    hideUser() {
      return this.inbox.hide_user || false;
    },
    showTokenField() {
      // Never show token input field - token is always auto-generated
      return false;
    },
  },
  watch: {
    inbox: {
      handler() {
        this.loadSettings();
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadSettings();
    if (this.isConfigured) {
      this.checkStatus();
    }
  },
  beforeUnmount() {
    this.stopQRCodePolling();
  },
  methods: {
    loadSettings() {
      this.settings = {
        quepasa_url: this.inbox.quepasa_url || '',
        quepasa_token: this.inbox.quepasa_token || '',
        quepasa_user: this.inbox.quepasa_user || '',
        quepasa_enable_group: this.inbox.quepasa_enable_group || false,
        quepasa_accept_call: this.inbox.quepasa_accept_call || false,
        quepasa_single_thread: this.inbox.quepasa_single_thread !== false,
        quepasa_send_agent_name: this.inbox.quepasa_send_agent_name !== false,
      };
      this.connectionStatus.connected = this.inbox.quepasa_connected || false;
      this.connectionStatus.wid = this.inbox.quepasa_wid || null;
    },
    async saveSettings() {
      this.isLoading = true;
      try {
        await QuepasaAPI.updateSettings(this.inbox.id, this.settings);
        await this.$store.dispatch('inboxes/get');
        useAlert(this.$t('INBOX_MGMT.QUEPASA.SETTINGS_SAVED'));
      } catch (error) {
        const errorMsg =
          error.response?.data?.error ||
          error.message ||
          this.$t('INBOX_MGMT.QUEPASA.SETTINGS_ERROR');
        useAlert(errorMsg);
      } finally {
        this.isLoading = false;
      }
    },
    async checkStatus() {
      if (!this.isConfigured) return;

      try {
        const response = await QuepasaAPI.getStatus(this.inbox.id);
        this.connectionStatus = response.data;
        if (this.connectionStatus.connected) {
          this.stopQRCodePolling();
          await this.$store.dispatch('inboxes/get');
        }
      } catch (error) {
        console.error('Failed to check status:', error);
      }
    },
    async connectQRCode() {
      if (!this.isConfigured) {
        useAlert(this.$t('INBOX_MGMT.QUEPASA.CONFIGURE_FIRST'));
        return;
      }

      this.isConnecting = true;
      this.qrCode = null;

      try {
        // Save settings first before connecting
        await QuepasaAPI.updateSettings(this.inbox.id, this.settings);
        await this.$store.dispatch('inboxes/get');

        const response = await QuepasaAPI.getQRCode(this.inbox.id);
        if (response.data.already_connected) {
          // Already connected, just update the status
          this.connectionStatus.connected = true;
          this.connectionStatus.wid = response.data.wid;
          await this.$store.dispatch('inboxes/get');
          useAlert(this.$t('INBOX_MGMT.QUEPASA.ALREADY_CONNECTED'));
        } else if (response.data.qr_code) {
          this.qrCode = response.data.qr_code;
          this.startCountdown(20);
          this.startQRCodePolling();
        } else {
          useAlert(this.$t('INBOX_MGMT.QUEPASA.QR_CODE_ERROR'));
        }
      } catch (error) {
        const errorMsg =
          error.response?.data?.error ||
          error.message ||
          this.$t('INBOX_MGMT.QUEPASA.QR_CODE_ERROR');
        useAlert(errorMsg);
      } finally {
        this.isConnecting = false;
      }
    },
    startCountdown(seconds) {
      this.countdown = seconds;
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          this.stopQRCodePolling();
          this.qrCode = null;
        }
      }, 1000);
    },
    startQRCodePolling() {
      this.qrCodeTimer = setInterval(async () => {
        await this.checkStatus();
      }, 10000);
    },
    stopQRCodePolling() {
      if (this.qrCodeTimer) {
        clearInterval(this.qrCodeTimer);
        this.qrCodeTimer = null;
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      this.countdown = 0;
    },
    async disconnect() {
      try {
        const response = await QuepasaAPI.disconnect(this.inbox.id);
        this.connectionStatus = {
          connected: false,
          wid: null,
          phone: null,
          name: null,
        };
        this.qrCode = null;
        await this.$store.dispatch('inboxes/get');
        // Show message from server or default
        const message =
          response.data?.message || this.$t('INBOX_MGMT.QUEPASA.DISCONNECTED');
        useAlert(message);
      } catch (error) {
        // Even on error, try to clear local state
        this.connectionStatus = {
          connected: false,
          wid: null,
          phone: null,
          name: null,
        };
        this.qrCode = null;
        await this.$store.dispatch('inboxes/get');
        useAlert(
          error.response?.data?.message ||
            error.message ||
            this.$t('INBOX_MGMT.QUEPASA.DISCONNECT_ERROR')
        );
      }
    },
    async setupWebhooks() {
      this.isLoading = true;
      try {
        await QuepasaAPI.setupWebhooks(this.inbox.id);
        await this.$store.dispatch('inboxes/get');
        useAlert(this.$t('INBOX_MGMT.QUEPASA.WEBHOOKS_CONFIGURED'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.QUEPASA.WEBHOOKS_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    toggleTokenVisibility() {
      this.showToken = !this.showToken;
    },
    async toggleGroups() {
      if (!this.isConnected) {
        // If not connected, just update local state - will be synced when saving
        return;
      }

      this.isTogglingGroups = true;
      try {
        await QuepasaAPI.updateSettings(this.inbox.id, {
          quepasa_enable_group: this.settings.quepasa_enable_group,
        });
        await this.$store.dispatch('inboxes/get');
        useAlert(
          this.settings.quepasa_enable_group
            ? this.$t('INBOX_MGMT.QUEPASA.GROUPS_ENABLED')
            : this.$t('INBOX_MGMT.QUEPASA.GROUPS_DISABLED')
        );
      } catch (error) {
        // Revert on error
        this.settings.quepasa_enable_group =
          !this.settings.quepasa_enable_group;
        const errorMsg =
          error.response?.data?.error ||
          error.message ||
          this.$t('INBOX_MGMT.QUEPASA.SETTINGS_ERROR');
        useAlert(errorMsg);
      } finally {
        this.isTogglingGroups = false;
      }
    },
    async toggleCalls() {
      if (!this.isConnected) {
        // If not connected, just update local state - will be synced when saving
        return;
      }

      this.isTogglingCalls = true;
      try {
        await QuepasaAPI.updateSettings(this.inbox.id, {
          quepasa_accept_call: this.settings.quepasa_accept_call,
        });
        await this.$store.dispatch('inboxes/get');
        useAlert(
          this.settings.quepasa_accept_call
            ? this.$t('INBOX_MGMT.QUEPASA.CALLS_ENABLED')
            : this.$t('INBOX_MGMT.QUEPASA.CALLS_DISABLED')
        );
      } catch (error) {
        // Revert on error
        this.settings.quepasa_accept_call = !this.settings.quepasa_accept_call;
        const errorMsg =
          error.response?.data?.error ||
          error.message ||
          this.$t('INBOX_MGMT.QUEPASA.SETTINGS_ERROR');
        useAlert(errorMsg);
      } finally {
        this.isTogglingCalls = false;
      }
    },
    async syncContacts() {
      if (!this.isConnected) {
        useAlert(this.$t('INBOX_MGMT.QUEPASA.SYNC_NOT_CONNECTED'));
        return;
      }

      this.isSyncing = true;
      try {
        const response = await QuepasaAPI.syncContacts(this.inbox.id);
        const data = response.data;

        if (data.success) {
          // Check if running in background
          if (data.background) {
            useAlert(
              data.message || this.$t('INBOX_MGMT.QUEPASA.SYNC_STARTED')
            );
          } else {
            const message = this.$t('INBOX_MGMT.QUEPASA.SYNC_SUCCESS', {
              contacts: data.contacts_created || 0,
              updated: data.contacts_updated || 0,
            });
            useAlert(message);
          }
        } else {
          useAlert(data.error || this.$t('INBOX_MGMT.QUEPASA.SYNC_ERROR'));
        }
      } catch (error) {
        const errorMsg =
          error.response?.data?.error ||
          error.message ||
          this.$t('INBOX_MGMT.QUEPASA.SYNC_ERROR');
        useAlert(errorMsg);
      } finally {
        this.isSyncing = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <!-- Connection Section -->
    <SettingsSection
      :title="$t('INBOX_MGMT.QUEPASA.CONNECTION.TITLE')"
      :sub-title="$t('INBOX_MGMT.QUEPASA.CONNECTION.SUBTITLE')"
    >
      <div class="flex flex-col gap-4">
        <!-- QR Code Section -->
        <div v-if="!isConnected" class="flex flex-col items-start gap-4">
          <div v-if="qrCode" class="flex flex-col items-center gap-2">
            <p class="text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.QUEPASA.SCAN_QR_CODE') }} {{ countdown }}s...
            </p>
            <img
              :src="qrCode"
              alt="QR Code"
              class="w-64 h-64 border rounded-lg"
            />
          </div>
          <NextButton
            v-else
            :label="$t('INBOX_MGMT.QUEPASA.CONNECT_QR_CODE')"
            :is-loading="isConnecting"
            :disabled="!isConfigured"
            @click="connectQRCode"
          />
          <p v-if="!isConfigured" class="text-sm text-n-ruby-11">
            {{ $t('INBOX_MGMT.QUEPASA.CONFIGURE_FIRST') }}
          </p>
        </div>

        <!-- Connected Status -->
        <div v-else class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 bg-n-teal-11 rounded-full" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.QUEPASA.CONNECTED') }}
            </span>
          </div>
          <div class="flex flex-col gap-1 text-sm text-n-slate-11">
            <p><strong>Token:</strong> {{ inbox.quepasa_token }}</p>
            <p><strong>WID:</strong> {{ displayWid }}</p>
          </div>
          <div class="flex gap-2 flex-wrap">
            <NextButton
              variant="danger"
              :label="$t('INBOX_MGMT.QUEPASA.DISCONNECT')"
              @click="disconnect"
            />
            <NextButton
              variant="secondary"
              :label="$t('INBOX_MGMT.QUEPASA.SYNC_CONTACTS')"
              :is-loading="isSyncing"
              icon="i-lucide-download"
              @click="syncContacts"
            />
          </div>
        </div>
      </div>
    </SettingsSection>

    <!-- Settings Section -->
    <SettingsSection
      :title="$t('INBOX_MGMT.QUEPASA.SETTINGS.TITLE')"
      :sub-title="$t('INBOX_MGMT.QUEPASA.SETTINGS.SUBTITLE')"
    >
      <div class="flex flex-col gap-4">
        <woot-input
          v-if="!hideUrl"
          v-model="settings.quepasa_url"
          :label="$t('INBOX_MGMT.QUEPASA.URL_LABEL')"
          :placeholder="$t('INBOX_MGMT.QUEPASA.URL_PLACEHOLDER')"
        />
        <div v-else class="text-sm text-n-slate-11">
          <strong>{{ $t('INBOX_MGMT.QUEPASA.URL_LABEL') }}:</strong>
          {{ $t('INBOX_MGMT.QUEPASA.URL_FROM_ENV') }}
        </div>

        <!-- Token field - only show if explicitly enabled via showTokenField -->
        <div v-if="showTokenField" class="relative">
          <woot-input
            v-model="settings.quepasa_token"
            :label="$t('INBOX_MGMT.QUEPASA.TOKEN_LABEL')"
            :placeholder="$t('INBOX_MGMT.QUEPASA.TOKEN_PLACEHOLDER')"
            :type="showToken ? 'text' : 'password'"
          />
          <button
            type="button"
            class="absolute right-3 top-9 text-n-slate-11 hover:text-n-slate-12"
            @click="toggleTokenVisibility"
          >
            <fluent-icon :icon="showToken ? 'eye-off' : 'eye'" size="16" />
          </button>
        </div>
        <p v-else class="text-sm text-n-slate-11">
          <strong>{{ $t('INBOX_MGMT.QUEPASA.TOKEN_LABEL') }}:</strong>
          {{ $t('INBOX_MGMT.QUEPASA.TOKEN_AUTO') }}
        </p>

        <woot-input
          v-if="!hideUser"
          v-model="settings.quepasa_user"
          :label="$t('INBOX_MGMT.QUEPASA.USER_LABEL')"
          :placeholder="$t('INBOX_MGMT.QUEPASA.USER_PLACEHOLDER')"
          type="email"
        />
        <div v-else class="text-sm text-n-slate-11">
          <strong>{{ $t('INBOX_MGMT.QUEPASA.USER_LABEL') }}:</strong>
          {{ $t('INBOX_MGMT.QUEPASA.USER_FROM_ENV') }}
        </div>

        <div class="flex flex-col gap-3">
          <label class="flex items-center gap-2">
            <input
              v-model="settings.quepasa_enable_group"
              type="checkbox"
              class="w-4 h-4"
              :disabled="isTogglingGroups"
              @change="toggleGroups"
            />
            <span>{{ $t('INBOX_MGMT.QUEPASA.ENABLE_GROUP') }}</span>
            <Spinner v-if="isTogglingGroups" size="tiny" />
          </label>

          <label class="flex items-center gap-2">
            <input
              v-model="settings.quepasa_accept_call"
              type="checkbox"
              class="w-4 h-4"
              :disabled="isTogglingCalls"
              @change="toggleCalls"
            />
            <span>{{ $t('INBOX_MGMT.QUEPASA.ACCEPT_CALL') }}</span>
            <Spinner v-if="isTogglingCalls" size="tiny" />
          </label>

          <label class="flex items-center gap-2">
            <input
              v-model="settings.quepasa_single_thread"
              type="checkbox"
              class="w-4 h-4"
            />
            <span>{{ $t('INBOX_MGMT.QUEPASA.SINGLE_THREAD') }}</span>
          </label>

          <label class="flex items-center gap-2">
            <input
              v-model="settings.quepasa_send_agent_name"
              type="checkbox"
              class="w-4 h-4"
            />
            <span>{{ $t('INBOX_MGMT.QUEPASA.SEND_AGENT_NAME') }}</span>
          </label>
        </div>

        <div class="flex gap-2">
          <NextButton
            :label="$t('INBOX_MGMT.QUEPASA.SAVE_SETTINGS')"
            :is-loading="isLoading"
            @click="saveSettings"
          />
          <NextButton
            v-if="isConnected"
            variant="secondary"
            :label="$t('INBOX_MGMT.QUEPASA.SETUP_WEBHOOKS')"
            :is-loading="isLoading"
            @click="setupWebhooks"
          />
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
